/**
 * Boss fights (the plan: https://claude.ai/artifact/1kUFGowqw38hVd1Emczty6).
 * A Legendary dinosaur (level 100 and up) can take on a boss in a short,
 * clicky fight. Phase 1 has one: Deinosuchus, the river terror, who lives in
 * the stream along the bottom of the yard.
 */

export type BossMoveId = 'snap' | 'roll' | 'submerge';

export interface BossMove {
  id: BossMoveId;
  /** What the warning says. */
  name: string;
  /** Seconds of wind-up before it lands; the Brace window is its last part. */
  windup: number;
  /** How often it is picked, against the other moves' weights. */
  weight: number;
}

export interface BossDef {
  id: string;
  name: string;
  /** "the River Terror": shown beside the name in the arena. */
  epithet: string;
  /** The title a dinosaur earns by beating it: "Spike the Croc-Slayer". */
  title: string;
  /** Health, in the units a level-100 bottom-tier dinosaur's tap does 10 of. */
  health: number;
  /** Lives in water: a swimmer's special does double against it. */
  water: boolean;
  /** Seconds on the clock. */
  seconds: number;
  /** Seconds between one move landing and the next wind-up starting. */
  gap: { min: number; max: number };
  moves: readonly BossMove[];
  /** Shards for a win (doubled for a flawless one). */
  shards: number;
  /** Fossils the first time it is ever beaten, by any dinosaur. */
  fossils: number;
  /** Its sprite in public/art/bosses/, drawn facing left. */
  art: string;
}

export const DEINOSUCHUS: BossDef = {
  id: 'boss:deinosuchus',
  name: 'Deinosuchus',
  epithet: 'the River Terror',
  title: 'Croc-Slayer',
  health: 6000,
  water: true,
  seconds: 60,
  gap: { min: 2.2, max: 3.6 },
  moves: [
    { id: 'snap', name: 'Snap', windup: 1.0, weight: 5 },
    { id: 'roll', name: 'Death Roll', windup: 1.3, weight: 2 },
    { id: 'submerge', name: 'Submerge', windup: 0.8, weight: 2 },
  ],
  shards: 20,
  fossils: 2,
  art: 'deinosuchus',
};

export const BOSSES: readonly BossDef[] = [DEINOSUCHUS];
export const BOSSES_BY_ID: ReadonlyMap<string, BossDef> = new Map(BOSSES.map((b) => [b.id, b]));

/**
 * Boss fights are not ready for players: the Challenge button only shows on
 * the dev server, never in a production build (not even with ?debug).
 */
export const BOSS_FIGHTS_LIVE = import.meta.env.DEV;

/** The level a dinosaur has to reach before it can fight: Legendary. */
export const BOSS_MIN_LEVEL = 100;
/** Real seconds a dinosaur rests after a fight, won or lost. */
export const BOSS_REST_SECONDS = 60 * 60;
/** The Victory Roar: the whole herd lays this many times as much, for this long. */
export const VICTORY_ROAR_MULT = 2;
export const VICTORY_ROAR_SECONDS = 10 * 60;
